import { createAsyncThunk } from '@reduxjs/toolkit';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { RootReducerState } from '../../redux/Reducers';
import axios from 'axios';
import { rejectWithApiError } from '../../utils/api';
import { CreatePRIDeviceDto, PRIDeviceType, UpdatePRIDeviceDto } from '../../types/pri-device/pri-device.type';
import { setModalContent, setShowModal } from '../utils/utils.slice';
import { UpdatePRIDevicePortDto } from '../../types/pri-device/pri-device-port.type';
import { additionalQueryParams } from '../../utils/utilities';

export const getPRIDevicesList = createAsyncThunk<
  PaginationType<PRIDeviceType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('priDevices/getPRIDevicesList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs
    );
    const mergedParams = { ...params, ...additionalParams };

    const response = await axios.get<PaginationType<PRIDeviceType>>(
      `${process.env.REACT_APP_API_URL}/pri-device`,
      controller ? { signal: controller.signal, params: mergedParams } : { params: mergedParams }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching PRI Devices!' });
  }
});

export const getPRIDeviceDetails = createAsyncThunk<PRIDeviceType, { id: number }, { state: RootReducerState }>(
  'priDevices/getPRIDeviceDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<PRIDeviceType>(`${process.env.REACT_APP_API_URL}/pri-device/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching PRI Device!' });
    }
  }
);

export const patchPRIDeviceDetails = createAsyncThunk<
  PRIDeviceType,
  { id: number; data: UpdatePRIDeviceDto },
  { state: RootReducerState }
>('priDevices/patchPRIDeviceDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<PRIDeviceType>(`${process.env.REACT_APP_API_URL}/pri-device/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating PRI Device!' });
  }
});

export const patchPRIDevicePortsDetails = createAsyncThunk<
  PRIDeviceType,
  { id: number; data: { ports: Array<UpdatePRIDevicePortDto> } },
  { state: RootReducerState }
>('priDevices/patchPRIDevicePortsDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<PRIDeviceType>(`${process.env.REACT_APP_API_URL}/pri-device/${id}/ports`, data);
    thunkAPI.dispatch(getPRIDeviceDetails({ id }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating PRI Device ports!' });
  }
});

export const postPRIDeviceOwnershipTransfer = createAsyncThunk<
  void,
  { id: number; data: { owner: { id: number } } },
  { state: RootReducerState; rejectValue: string }
>('priDevices/postATAOwnershipTransfer', async ({ id, data }, thunkAPI) => {
  try {
    if (!data.owner?.id) {
      return rejectWithApiError({ thunkAPI, error: null, fallback: 'Owner organization is required!' });
    }

    await axios.post(`${process.env.REACT_APP_API_URL}/pri-device/${id}/ownership-transfer`, data);
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during PRI Device ownership transfer!' });
  }
});

export const postPRIDeviceDetails = createAsyncThunk<
  PRIDeviceType,
  { data: CreatePRIDeviceDto },
  { state: RootReducerState }
>('priDevices/postPRIDeviceDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<PRIDeviceType>(`${process.env.REACT_APP_API_URL}/pri-device`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating PRI Device!' });
  }
});

export const deletePRIDeviceDetails = createAsyncThunk<
  { fetchList: boolean },
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('priDevices/deletePRIDeviceDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    await axios.delete<{ success: boolean }>(`${process.env.REACT_APP_API_URL}/pri-device/${id}`);
    if (fetchList) {
      thunkAPI.dispatch(getPRIDevicesList(thunkAPI.getState().priDevicesReducer.priDevicesListRouteParams));
    }
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return { fetchList };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during removing PRI Device!' });
  }
});
